import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Phase B2.5c — typed hook + helpers for the SA module catalog.
 *
 * The catalog is served by `GET /super-admin/modules` (BE Phase B2.5b)
 * with a deploy-stable ETag (`module-catalog-v<version>`) and an edge
 * `Cache-Control: max-age=300, stale-while-revalidate=600` header.
 * That makes it ideal for an aggressively-cached TanStack Query — the
 * BE catalog only changes when we redeploy, so a 5-minute staleTime
 * matches the cache header without surprising anyone.
 *
 * Responsibilities split:
 *
 *   - this file owns the API client, types, hook and indexing helpers
 *   - UI components (CreateTenantForm, TenantDetailPage's ModulesCard)
 *     consume the indexed view and stay free of API plumbing
 *
 * Why we filter client-side instead of always calling
 * `/super-admin/modules?vertical=<x>`: the FE's CreateTenantForm
 * lets the SA flip the vertical at will, and switching shouldn't
 * trigger a re-fetch — we already have the full catalog locally.
 * The `?vertical=` flavour exists for FE bundles that only ever
 * render one vertical (out of scope today, but keeps the API tidy).
 */

export type Vertical = "school" | "coaching_institute" | "study_centre";

export type ModuleCategory = "core" | "academic" | "operations" | "finance_hr" | "integrations";

export interface ModuleSpec {
  key: string;
  name: string;
  description: string;
  /** Lucide-react icon name, e.g. "Users", "Sparkles". */
  icon: string;
  category: ModuleCategory;
  /** Verticals where this module is offered. */
  available_for: Vertical[];
  /** Verticals where this module is enabled by default at tenant create. */
  default_for: Vertical[];
}

export interface ModuleCatalog {
  modules: ModuleSpec[];
  version: number;
  count: number;
}

/** Indexed view computed once per fetch — cheaper than re-deriving on every render. */
export interface IndexedCatalog {
  raw: ModuleCatalog;
  byKey: Map<string, ModuleSpec>;
  byCategory: Record<ModuleCategory, ModuleSpec[]>;
}

const CATEGORY_ORDER: ModuleCategory[] = [
  "core",
  "academic",
  "operations",
  "finance_hr",
  "integrations",
];

export const CATEGORY_LABEL: Record<ModuleCategory, string> = {
  core: "Core",
  academic: "Academic",
  operations: "Operations",
  finance_hr: "Finance & HR",
  integrations: "Integrations & SSO",
};

export const CATEGORY_DESCRIPTION: Record<ModuleCategory, string> = {
  core: "Day-one essentials — every tenant starts with these enabled.",
  academic: "Curriculum-side modules — lesson plans, library, events, alumni follow-up.",
  operations: "Physical-plant modules — transport, hostel, inventory, vendor, sport.",
  finance_hr: "Money + people-ops — accounting, payroll, HR, GST, audit export, KYC.",
  integrations: "External system bridges — SSO, SIS sync, DPDPA consent ledger.",
};

function indexCatalog(raw: ModuleCatalog): IndexedCatalog {
  const byKey = new Map<string, ModuleSpec>();
  const byCategory: Record<ModuleCategory, ModuleSpec[]> = {
    core: [],
    academic: [],
    operations: [],
    finance_hr: [],
    integrations: [],
  };
  for (const spec of raw.modules) {
    byKey.set(spec.key, spec);
    byCategory[spec.category].push(spec);
  }
  return { raw, byKey, byCategory };
}

/**
 * Fetch the SA module catalog. Cached for 5 minutes (matches BE
 * `Cache-Control: max-age=300`). Pass `vertical` to ask the BE to
 * pre-filter — usually you DON'T want this on the SA portal because
 * the SA can flip vertical mid-flow.
 */
export function useModuleCatalog(vertical?: Vertical) {
  return useQuery<ModuleCatalog, Error, IndexedCatalog>({
    queryKey: ["super-admin", "modules", vertical ?? "all"],
    queryFn: () => {
      const url = vertical
        ? `/super-admin/modules?vertical=${encodeURIComponent(vertical)}`
        : "/super-admin/modules";
      return api.get<ModuleCatalog>(url);
    },
    select: indexCatalog,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Helper: is this module available for the tenant's vertical given
 * the current override flag? Mirrors the BE check in
 * `app.domains.tenant.modules.is_module_available_for_vertical` plus
 * the per-tenant override semantics from
 * `TenantFeatureFlags.module_override_vertical_matrix`.
 */
export function isAvailable(
  spec: ModuleSpec,
  vertical: Vertical,
  overrideEnabled: boolean,
): boolean {
  if (overrideEnabled) return true;
  return spec.available_for.includes(vertical);
}

/**
 * Helper: the default-on set for a given vertical. Used by the
 * CreateTenantForm to pre-select the right modules when the SA picks
 * a vertical. Mirrors BE `default_modules_for_vertical()`.
 */
export function defaultModulesForVertical(
  catalog: IndexedCatalog,
  vertical: Vertical,
): Set<string> {
  const defaults = new Set<string>();
  for (const spec of catalog.raw.modules) {
    if (spec.default_for.includes(vertical)) defaults.add(spec.key);
  }
  return defaults;
}

export { CATEGORY_ORDER };
